import React from 'react';
import styled from 'styled-components';
import { 
  Brain, 
  FileText, 
  BarChart3, 
  Zap, 
  Shield, 
  Globe2,
  Users,
  Award,
  TrendingUp,
  Clock,
  Target,
  Database
} from 'lucide-react';
import { theme } from '../styles/theme';

const CardsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${theme.spacing[4]};

  @media (max-width: ${theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: ${theme.spacing[3]};
  }
`;

const InfoCard = styled.div`
  background: ${theme.colors.surface};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[5]};
  transition: all ${theme.transitions.base};
  position: relative;
  overflow: hidden;

  &:hover {
    border-color: ${theme.colors.primary[300]};
    transform: translateY(-2px);
    box-shadow: ${theme.shadows.lg};
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, ${theme.colors.primary[400]}, ${theme.colors.primary[600]});
    opacity: 0;
    transition: opacity ${theme.transitions.base};
  }

  &:hover::before {
    opacity: 1;
  }
`;

const CardIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary[100]};
  color: ${theme.colors.primary[600]};
  margin-bottom: ${theme.spacing[4]};
  transition: all ${theme.transitions.base};

  ${InfoCard}:hover & {
    background: ${theme.colors.primary[500]};
    color: white;
    transform: scale(1.1);
  }
`;

const CardTitle = styled.h3`
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[3]};
  line-height: ${theme.typography.lineHeight.tight};
`;

const CardDescription = styled.p`
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.textSecondary};
  line-height: ${theme.typography.lineHeight.normal};
  margin-bottom: ${theme.spacing[4]};
`;

const CardFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

const Feature = styled.li`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.textMuted};
  margin-bottom: ${theme.spacing[2]};

  &:last-child {
    margin-bottom: 0;
  }

  &::before {
    content: '✓';
    display: flex;
    align-items: center;
    justify-content: center;
    width: 16px;
    height: 16px;
    border-radius: ${theme.borderRadius.full};
    background: ${theme.colors.success[100]};
    color: ${theme.colors.success[600]};
    font-size: 10px;
    font-weight: bold;
    flex-shrink: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: ${theme.spacing[6]};
  text-align: center;
  font-family: ${theme.typography.fontFamily.display};
`;

const SectionSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.textSecondary};
  text-align: center;
  margin-bottom: ${theme.spacing[8]};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const PlatformInfoCards: React.FC = () => {
  const platformFeatures = [
    {
      icon: <Brain size={24} />,
      title: 'AI-Powered Analysis',
      description: 'Advanced machine learning algorithms analyze tender documents with 95% accuracy.',
      features: [
        'Automatic requirement extraction',
        'Intelligent scoring algorithms',
        'Multi-language support',
        'Context-aware analysis'
      ]
    },
    {
      icon: <FileText size={24} />,
      title: 'Document Processing',
      description: 'Handle any document format with intelligent parsing and content extraction.',
      features: [
        'PDF, Word, Excel support',
        'OCR for scanned documents',
        'Structured data extraction',
        'Version comparison'
      ]
    },
    {
      icon: <BarChart3 size={24} />,
      title: 'Performance Analytics',
      description: 'Comprehensive insights and reporting to improve your tender success rate.',
      features: [
        'Win rate analytics',
        'Competitor analysis',
        'Response quality scoring',
        'Historical performance'
      ]
    },
    {
      icon: <Zap size={24} />,
      title: 'Response Generation',
      description: 'Generate compelling, tailored responses using proven winning strategies.',
      features: [
        'Template library',
        'Custom response styles',
        'Compliance checking',
        'Quality optimization'
      ]
    },
    {
      icon: <Database size={24} />,
      title: 'Tender Bank',
      description: 'Build and maintain a searchable library of successful tender responses.',
      features: [
        'Centralized storage',
        'Smart search & filtering',
        'Success pattern analysis',
        'Team collaboration'
      ]
    },
    {
      icon: <Shield size={24} />,
      title: 'Enterprise Security',
      description: 'Bank-grade security ensuring your sensitive tender data is protected.',
      features: [
        'End-to-end encryption',
        'SOC 2 compliance',
        'Access controls',
        'Audit logging'
      ]
    }
  ];

  const successMetrics = [
    {
      icon: <TrendingUp size={20} />,
      title: 'Success Stories',
      description: 'Organizations using OpenTender AI see measurable improvements.',
      features: [
        '10x faster document analysis',
        '40% increase in win rates',
        '60% reduction in response time',
        '95% user satisfaction'
      ]
    },
    {
      icon: <Users size={20} />,
      title: 'Global Reach',
      description: 'Trusted by organizations worldwide across various industries.',
      features: [
        '50+ countries served',
        '1000+ active users',
        'Government & private sector',
        '24/7 global support'
      ]
    }
  ];

  return (
    <div>
      <SectionTitle>Platform Capabilities</SectionTitle>
      <SectionSubtitle>
        Discover the powerful features that make OpenTender AI the leading solution for tender analysis and response generation.
      </SectionSubtitle>

      <CardsContainer>
        {platformFeatures.map((feature, index) => (
          <InfoCard key={index}>
            <CardIcon>{feature.icon}</CardIcon>
            <CardTitle>{feature.title}</CardTitle>
            <CardDescription>{feature.description}</CardDescription>
            <CardFeatures>
              {feature.features.map((item, itemIndex) => (
                <Feature key={itemIndex}>{item}</Feature>
              ))}
            </CardFeatures>
          </InfoCard>
        ))}
      </CardsContainer>

      <div style={{ marginTop: theme.spacing[12] }}>
        <SectionTitle>Why Choose OpenTender AI?</SectionTitle>
        <CardsContainer>
          {successMetrics.map((metric, index) => (
            <InfoCard key={index}>
              <CardIcon>{metric.icon}</CardIcon>
              <CardTitle>{metric.title}</CardTitle>
              <CardDescription>{metric.description}</CardDescription>
              <CardFeatures>
                {metric.features.map((item, itemIndex) => (
                  <Feature key={itemIndex}>{item}</Feature>
                ))}
              </CardFeatures>
            </InfoCard>
          ))}
        </CardsContainer>
      </div>
    </div>
  );
};

export default PlatformInfoCards;